import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular/standalone';
import { environment } from '../environments/environment';
import { UtilsService } from './utils.service';

// TypeScript declarations for the global Chargebee object loaded from chargebee.js script tag
declare global {
  interface Window {
    Chargebee: ChargebeeStatic;
  }
}

interface ChargebeeStatic {
  init(options: { site: string; enableGTMTracking?: boolean }): ChargebeeInstance;
  getInstance(): ChargebeeInstance;
}

interface ChargebeeInstance {
  openCheckout(options: ChargebeeCheckoutOptions): void;
  createChargebeePortal(): ChargebeePortal;
  setPortalSession(callback: () => Promise<any>): void;
  closeAll(): void;
}

interface ChargebeeCheckoutOptions {
  hostedPage: () => Promise<any>;
  success?: (hostedPageId: string) => void;
  error?: (error: any) => void;
  close?: () => void;
  step?: (step: string) => void;
}

interface ChargebeePortal {
  open(options?: ChargebeePortalOptions): void;
}

interface ChargebeePortalOptions {
  close?: () => void;
  visit?: (sectionType: string) => void;
  loaded?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ChargebeeService {

  private cbInstance: ChargebeeInstance | null = null;

  constructor(
    private utils: UtilsService,
    private platform: Platform
  ) {}

  // initialize Chargebee instance with site from environment config
  // only on desktop web — iframes are unreliable on native WebViews and mobile PWAs
  public init(): void {
    if (Capacitor.isNativePlatform() || this.platform.is('mobile')) {
      return;
    }
    try {
      this.cbInstance = window.Chargebee.init({
        site: environment.chargebeeSite
      });
      this.utils.logInfo('ChargebeeService: initialized with site ' + environment.chargebeeSite);
    } catch (error) {
      this.utils.logDebug('ChargebeeService: init failed, will use URL redirect');
    }
  }

  // retry initialization if the async script wasn't loaded when init() first ran
  // waits up to 3 seconds for chargebee.js to become available
  public async ensureInitialized(): Promise<boolean> {
    if (this.cbInstance) return true;
    if (Capacitor.isNativePlatform() || this.platform.is('mobile')) {
      return false;
    }
    // wait for the async script to load, checking every 100ms up to 3 seconds
    for (let i = 0; i < 30; i++) {
      if (window.Chargebee) {
        try {
          this.cbInstance = window.Chargebee.init({ site: environment.chargebeeSite });
          this.utils.logInfo('ChargebeeService: late init succeeded after ' + (i * 100) + 'ms');
          return true;
        } catch (error) {
          this.utils.logDebug('ChargebeeService: late init failed', error);
          return false;
        }
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    this.utils.logDebug('ChargebeeService: chargebee.js did not load within 3s');
    return false;
  }

  // check if client-side Chargebee is available (web/PWA only, not native)
  public isAvailable(): boolean {
    return this.cbInstance !== null;
  }

  // open checkout as an in-app overlay
  // Reason: openCheckout must be called synchronously from the user's click handler.
  // Chargebee opens the overlay immediately, then calls the hostedPage callback
  // to fetch data asynchronously. If we fetched first and opened second, mobile
  // browsers and PWAs would block the overlay as an unauthorized popup.
  public async openCheckout(
    fetchHostedPage: () => Promise<any>,
    onSuccess?: () => void
  ): Promise<void> {
    if (!this.cbInstance) {
      await this.ensureInitialized();
    }
    if (!this.cbInstance) {
      this.utils.logError('ChargebeeService: openCheckout called but not initialized');
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.cbInstance!.openCheckout({
        hostedPage: () => fetchHostedPage(),
        success: (hostedPageId: string) => {
          this.utils.logInfo('ChargebeeService: checkout success, hostedPageId=' + hostedPageId);
          // Reason: Chargebee shows a confirmation screen with a close button after success
          // instead of auto-closing. closeAll() dismisses the overlay immediately so we can
          // refresh subscription data without waiting for the user to click close.
          this.cbInstance!.closeAll();
          if (onSuccess) onSuccess();
          resolve();
        },
        error: (error: any) => {
          this.utils.logError('ChargebeeService: checkout error', error);
          reject(error);
        },
        close: () => {
          this.utils.logInfo('ChargebeeService: checkout closed');
          resolve();
        }
      });
    });
  }

  // open account portal as an in-app overlay
  // Reason: same popup blocker constraint as checkout — openPortal must be called
  // synchronously from the click handler. The portal session fetch happens inside
  // Chargebee's setPortalSession callback, after the overlay is already open.
  public async openPortal(
    fetchPortalSession: () => Promise<any>,
    onClose?: () => void
  ): Promise<void> {
    if (!this.cbInstance) {
      await this.ensureInitialized();
    }
    if (!this.cbInstance) {
      this.utils.logError('ChargebeeService: openPortal called but not initialized');
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.cbInstance!.setPortalSession(fetchPortalSession);
      const portal = this.cbInstance!.createChargebeePortal();
      portal.open({
        close: () => {
          this.utils.logInfo('ChargebeeService: portal closed');
          if (onClose) onClose();
          resolve();
        }
      });
    });
  }
}
